// ─────────────────────────────────────────────────────────────────────────────
// WHAT THE CARRIER SHOULD HAVE PAID — and it is the carrier's OWN number
//
// Kevin, 2026-09-24: *"For Claims cross check I want to see The Farm Name, then a drill
// down with a separate for crop and county line with multiple columns showing our
// estimate, AIP Paid, Difference… The reason this tool is important is that NAU forgot to
// pay an entire county of PP for Sullivan Farms, they missed Monroe county. The farmer
// ended up catching it but we need to catch it first."*
//
// ⚠ THE PREVENT-PLANT EXPECTATION IS NOT AN ESTIMATE. A prevent-plant loss pays the
// prevent-plant guarantee — there is no production to count against it — and the AIP
// STATES that guarantee per unit, in dollars, on `crop_coverage_units.liability` where
// `is_prevent_plant`. So the "expected" column is the carrier's own figure read back at
// it, with no model, no test weight, no price and no assumption of ours.
//
// Measured on the live CY2026 book 2026-09-24, over the 115 prevent-plant cells the
// carrier has paid: **113 match the stated guarantee TO THE DOLLAR.** Median ratio
// 1.0000, p25 1.0000, p75 1.0000. That is what makes a difference here worth a phone
// call rather than worth explaining away.
//
// ⚠ AND A HARVEST LOSS IS DELIBERATELY NOT ESTIMATED. It pays the guarantee LESS
// production to count, and we do not hold the production to count — so harvest cells
// report NOT ESTIMATED and are counted, never compared against a number we would have
// had to invent. `claimCrossCheck.js` already refuses the same comparison.
//
// ⚠⚠ THE REASON CHANGED ON 2026-09-29 AND THE REFUSAL DID NOT. Until sql/040's repair
// ran, the honest sentence was "the production is a settlement fact this book does not
// hold". That is no longer true: `indemnity_lines.total_production` now carries P26 #17
// Total Production Amount on 3,389 rows, and 219 of the 221 harvest-paid cells have one.
// A reader seeing that column populated will reasonably assume the estimate is now
// trivial. IT IS NOT, and the difference was MEASURED rather than argued:
//
//     indemnity  =?=  liability − (total_production × implied price)
//
//   19 testable cells (the rest have no surviving crop_coverage row for their year)
//   model/paid   p10 1.00 · p25 1.03 · MEDIAN 1.57 · p75 2.32 · p90 7.26
//   within 5% of paid: 4 of 19          NEGATIVE model (over-subtracted): 0 of 19
//
// THE ONE-SIDEDNESS IS THE FINDING. The model never lands BELOW what was paid — p10 is
// exactly 1.00 — so it is not noisy, it is biased in one direction. That is the
// signature of subtracting HARVESTED production where the settlement subtracts
// PRODUCTION TO COUNT, which also includes appraisals on unharvested acres, production
// added back for uninsured causes, and quality adjustment. Production to count is
// >= harvested production, so using the smaller figure overstates the loss every time.
// The four cells that do land at 1.00 are plan 01/90 (POTATOES, OATS) where the two
// coincide; the wide ones are plan 02 and plan 88, and an 88 is an ECO area-plan line
// that does not settle on individual production at all.
//
// So P26 #17 is production, and it is NOT the production to count. Estimating a harvest
// loss needs a figure no record we hold carries — the adjuster's worksheet does. Until
// one arrives this stays refused, and it is now refused on evidence rather than on an
// absence. `tools/check-modelled-expected.js` asserts the refusal so it cannot be
// quietly lifted on the strength of a populated column.
// ─────────────────────────────────────────────────────────────────────────────

export const RULE_VERSION = "pp-stated-guarantee-v1";

export const BASIS = Object.freeze({
  PP_STATED: "pp_stated_guarantee",   // the AIP's own per-unit prevent-plant guarantee
  NOT_ESTIMATED: "not_estimated",     // we hold no sourced expectation for this stage
});

export const LANE = Object.freeze({
  NOTHING_PAID: "nothing_paid",       // a loss reported, no paid line at all
  DIFFERS: "differs",                 // paid, and not what the carrier stated
  AGREES: "agrees",                   // paid exactly what the carrier stated
  NOT_ESTIMATED: "not_estimated",     // paid, but we have no sourced figure to compare
});

// Prevent-plant stage codes: ONE shared rule, src/claims/ppStages.js (sourced from RMA's
// stage code list). `P` is "Uninsured Loss" there and is NOT prevent plant.
import { isPreventPlantStage as isPpStageShared } from "./ppStages";
export const isPreventPlantStage = (s) => isPpStageShared(s);

// ONE prevent-plant rate rule, shared with claimEstimate.js and ppAdjuster.js.
import { ppDollarsPerAcre } from "./ppRate.js";

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
// Money in integer cents. Float dollars accumulate error across the dozens of lines a
// single claim carries — this file already records $19.99 across six lines landing at
// 29.999999999999993 elsewhere in the book.
const cents = (v) => Math.round((num(v) || 0) * 100);
const dollars = (c) => c / 100;

// ── SUPERSEDED LINES (2026-09-24) ────────────────────────────────────────────
//
// ⚠ `m13_loss_detail` CARRIES BOTH THE OLD AND THE NEW ADJUSTMENT, AND SUMMING THEM
// DOUBLE-COUNTS. When a carrier RE-ADJUSTS a claim it re-keys every line: a new
// `loss_detail_key` and a new `acreage_key` for the same work. The mirror upserts on
// `loss_detail_key`, so the superseded rows never go away.
//
// Sullivan Farms' CY2026 claim 00000066 is the worked case, and it is the very claim
// Kevin caught: ten lines synced 2026-09-15 and eleven synced 2026-09-23, with four of
// five Livingston dry-bean lines IDENTICAL between the two batches (20.37 ac/$3,019,
// 11 ac/$1,630, 5.04 ac/$747) and MONROE SOYBEANS present only in the later one — the
// carrier re-issuing the whole claim to add the county it had missed. Summed raw, his
// three Livingston cells read exactly 2.000x the stated guarantee.
//
// The discriminator is the GENERATION: within one (aip, crop year, claim, coverage,
// stage), keep only the lines from the newest sync. That is POSITIVE evidence — a newer
// generation demonstrably exists — rather than "this row looks old", so a cell the
// carrier has only ever adjusted once is untouched.
//
// Applying it takes the book from 110 to 113 of 115 prevent-plant cells matching the
// carrier's own stated guarantee to the dollar.
export const GENERATION_WINDOW_MS = 24 * 60 * 60 * 1000;

export function currentLines(rows) {
  const src = (rows || []).filter(Boolean);
  const newest = new Map();
  const key = (r) => [r.aip, r.crop_year, r.claim_number, r.iif_key, r.stage_code].join("|");
  for (const r of src) {
    const t = Date.parse(r.synced_at || "") || 0;
    const k = key(r);
    if (!newest.has(k) || t > newest.get(k)) newest.set(k, t);
  }
  const kept = [], superseded = [];
  for (const r of src) {
    const t = Date.parse(r.synced_at || "") || 0;
    // No sync stamp at all means we cannot say it is superseded, so it is KEPT — the
    // absence of evidence is not evidence, and dropping a real payment would put a
    // grower on a "they never paid you" list.
    if (!t) { kept.push(r); continue; }
    (t >= newest.get(key(r)) - GENERATION_WINDOW_MS ? kept : superseded).push(r);
  }
  return { kept, superseded };
}

// ── OUR OWN FIGURE, COMPUTED — NOT THEIRS READ BACK ──────────────────────────
//
// Kevin, 2026-09-28: *"Using the AIPs number is not a cross check… I want us to
// calculate what we expect their claim to be and then compare it against what actually
// gets paid… if we use the AIPs numbers for both columns, that's not a check at all."*
//
// He is right, and it was true of the whole page: `expectedForCell` below reads
// `crop_coverage_units.liability` — the carrier's own stored figure — so both columns
// came from one document and a WRONG LIABILITY was invisible. Both columns inherited it
// and the page reported "agrees to the dollar".
//
// This is the independent one. FCIC-25370 ¶75(1)(a):
//
//     PP indemnity = PP factor x (approved yield x coverage level x PROJECTED price)
//                  x prevent-plant acres x share
//
// ⚠ THE TWO INPUTS THAT MAKE IT A CHECK COME FROM RMA, NOT THE AIP: the published
// projected price (our daily ADM pull into `rma_prices`) and the prevent-plant factor.
// Everything else — acres, approved yield, coverage level, share — is a policy term the
// grower agreed to. So a carrier that mis-prices or mis-factors a claim disagrees with
// this, which is the whole point.
//
// ⚠⚠ `basis: "aip_stated"` IS REFUSED OUTRIGHT, AND THAT REFUSAL IS THE FEATURE.
// `resolvePpFactor` reads the AIP's own `acre_guarantee_qty` first — which is liability
// rearranged — and it is the BEST answer for "what factor does this policy carry" and
// the WORST for "did they compute it correctly". Accepting it here would reintroduce
// exactly the defect Kevin named, one layer down, where nobody would see it.
//
// Measured on the live CY2026 book 2026-09-28 before this shipped, back-solving
// `liability / (acres x APH x level x share)` over all 177 prevent-plant units:
//   CORN grain    2.5410 = $4.62 x 0.55     exact
//   SOYBEANS      6.6540 = $11.09 x 0.60    exact
//   CORN silage  21.0370 = $38.25 x 0.55    exact
//   WHEAT         3.7388 = $5.76 x 0.649    the PF buy-up
// and the spread around those (corn ran 1.40-4.02 across 63 distinct values) is elected
// terms — a reduced price election, and one organic unit on practice 713 — not error.
// So this reproduces the carrier on today's book. It will find nothing until the day it
// does, and that day is what it exists for.
//
// PURE: the caller supplies `model`, because this module must not reach for RMA's tables
// itself — App.jsx owns `resolveRmaPrice` and `resolvePpFactor`, and a second copy of
// either is how they drift.
// ── crops RMA publishes at more than one price by TYPE ───────────────────────
// The value is the type the single stored price actually represents; anything
// else on the same crop is a different product and is refused rather than
// priced. MEASURED from `rma_prices` CY2026 NY, not assumed: commodity 0041
// (corn) publishes t016 and t026, and 0047 (dry beans) publishes t303, t304,
// t305, t308, t309, t311 and t318 at prices from $0.28 to $0.76. Extending this
// map is how a crop gets added; making it empty is how the guard dies.
const MULTI_TYPE_PRICE_CROPS = {
  CORN: new Set(["016", "16", ""]),          // 016 GRAIN is what $4.62/bu is
  "DRY BEANS": new Set(["308", ""]),         // 308 is the $0.43 the book back-solves to
};
const cropKey = (c) => String(c == null ? "" : c).trim().toUpperCase();

export function modelledPpExpectation(units, model = {}) {
  const pp = (units || []).filter((u) => u && u.is_prevent_plant);
  const px = num(model.projectedPrice);
  const missing = [];

  if (!pp.length) return { available: false, missing: ["no prevent-plant units on this coverage"], expected: null };
  if (px == null || px <= 0) missing.push("RMA's published projected price for this crop and year");

  // ⚠ REFUSED, NOT PREFERRED. See the note above — this rung is the AIP's own guarantee
  // quantity, so an estimate built on it agrees with them by construction.
  if (model.factorBasis === "aip_stated") {
    missing.push("an independent prevent-plant factor — the only one on file is read back "
               + "out of the AIP's own stated guarantee, which cannot disagree with them");
  }
  const fLo = num(model.ppFactorLow), fHi = num(model.ppFactorHigh);
  const exact = model.factorExact === true && fLo != null && fHi != null && fLo === fHi;
  if (fLo == null || fHi == null) missing.push("a prevent-plant factor for this crop year");

  // Every unit must carry its own terms. A cell where one unit is short is reported
  // short rather than estimated on the ones that happen to be complete.
  const short = new Set();
  for (const u of pp) {
    if (!(num(u.acres) > 0)) short.add("reported prevent-plant acres");
    if (!(num(u.aph_yield) > 0)) short.add("an approved yield");
    if (!(num(u.coverage_level) > 0)) short.add("a coverage level");
    if (!(num(u.share) > 0)) short.add("an insured share");
  }
  for (const s of short) missing.push(s + " on every unit");
  if (missing.length) return { available: false, missing, expected: null };

  // ⚠ THE GUARANTEE IS ROUNDED TO 0.1 bu/ac BEFORE IT MEETS THE ACRES, AND THAT ROUNDING
  // IS SOURCED RATHER THAN FITTED. RMA states a per-acre GUARANTEE QUANTITY (P11 #120,
  // `acre_guarantee_qty`) with every adjustment already in it, and the dollars follow from
  // it — so the arithmetic is round-then-multiply, not multiply-then-round.
  //
  // Verified against the AIP's OWN stated field, not back-solved from liability: of the
  // 123 CY2026 prevent-plant units that carry `acre_guarantee_qty`, **118 are exactly one
  // decimal place**. Computing it the other way is wrong by a few dollars on every line —
  // measured over seven real units it left residuals of $18 to $45 on ~$120,000, which is
  // small enough to look like noise and would have made every row differ by a little
  // forever. A check whose rows never tie exactly is a check nobody trusts.
  //
  // ⚠ AND THE STORED `acre_guarantee_qty` IS DELIBERATELY NOT USED. It is the AIP's own
  // figure — the same rung `resolvePpFactor` ranks first and this function refuses above.
  // We recompute it; where ours and theirs disagree, THAT is a finding, and using theirs
  // would be the one way to guarantee it never surfaces.
  // ⚠ IMPORTED, NOT RE-DECLARED. This rounding was defined here and NOWHERE else, while
  // `estimatePreventPlant` priced the same acre without it — $1,946 apart across the 148
  // live prevent-plant units, $369.69 on the worst one. src/claims/ppRate.js is the one
  // rule now and all three callers take it from there.
  // ── ⚠ THE PRICE ELECTION. A CAT LINE PAYS AT 55% AND THIS MULTIPLIED BY 100% ──
  //
  // Kevin, 2026-09-29, looking at MARQUART BROS on the cross-check: *"Are they
  // CAT policies?"* — and one of them is. The election is a multiplier on the
  // PRICE, not on the guarantee quantity, so it lands here rather than inside
  // `acreGuaranteeQty`.
  //
  // Measured over that farm, our figure against the carrier's own stated
  // liability, per farm x crop:
  //     AARON       POTATOES  1.000      MARQUART BROS  CORN      1.000
  //     THOMAS      POTATOES  1.000      MARQUART DAIRY CORN      1.000
  //     MARQUART BROS POTATOES  1.818    <- and 1.818 is exactly 1 / 0.55
  // Four of five tied to the dollar and the fifth was a CAT line: plan 90,
  // coverage 0.50, `price_election_pct` 0.55. Nothing else differed.
  //
  // ⚠ IT IS NOT A MARQUART PROBLEM. Book-wide, 102 of 2,616 coverage rows carry
  // a reduced election — 49 at 0.50, 41 at 0.55, 4 at 0.75, 6 at 0.90, 2 at
  // 0.80 — and every one of them was being modelled at full price, always in
  // the direction that invents a shortfall the carrier does not owe.
  //
  // ⚠ AND IT IS NOT READABLE FROM THE UNITS. `crop_coverage_units.price_election`
  // is NULL on all 45 of that farm's prevent-plant units; the election lives on
  // `crop_coverage.price_election_pct`, which the caller passes in. An ABSENT
  // election is treated as 100% — that is what the other 2,514 rows are — but a
  // value outside (0, 1] is refused rather than applied, because a bad multiplier
  // here silently scales a grower's expected indemnity.
  // ── ⚠ THE PRICE IS PER CROP **AND TYPE**, AND ONE CROP IS TWO PRODUCTS ──────
  //
  // `resolveRmaPrice` keys on crop alone, so a CORN cell gets $4.62/bu whether
  // the units are GRAIN (type 016, bu/ac) or SILAGE (type 026, ton/ac). CY2026
  // holds 6 prevent-plant silage units over 424.4 acres with an APH around 18
  // TON — modelled against a bushel price they read at a ratio of 0.120 to the
  // carrier's own figure, which is 4.62/38.25. That is not a finding about the
  // carrier; it is this model quoting the wrong commodity, and it lands on the
  // screen as an eight-fold discrepancy.
  //
  // Dry beans are the same shape: types 303, 304 and 308 publish at $0.58,
  // $0.38 and $0.76 and the model has one number for all three.
  //
  // ⚠ SO IT REFUSES RATHER THAN GUESSES. This is the rule this repo already
  // enforces everywhere else — "never merge crop TYPES, and never average
  // across units of measure" — and the caller cannot currently resolve a
  // type-aware price, so the honest answer is a named absence. A wrong number
  // on a claim cross-check is worse than a blank, because the blank says so.
  // ⚠ THE GUARD MUST READ THE UNITS IT SUMS, NOT ONLY THE COVERAGE'S OWN TYPE.
  // Found 2026-09-29. `model.typeCode` is the type of ONE coverage — the base
  // row `buildExpectedBook` picks for the cell — while `pp` below is every
  // prevent-plant unit in that cell, and the cell key is
  // (policy, county, crop) with NO TYPE. So a cell whose base coverage is 016
  // sailed past this refusal and then priced a 026 unit at the grain price.
  //
  // Measured over CY2026: 105 cells carry prevent plant, 55 span more than one
  // type_code ($2,303,416 of PP liability), and 16 hold corn 016 AND 026. Of
  // those 16, four refuse correctly on the coverage's own type and eleven have
  // PP units that are all grain — so exactly ONE cell was live:
  //
  //     WYOMING  base coverage 016  ·  PP unit type 026  ·  APH 21 TON
  //     $30,091 of PP liability, modelled at the corn GRAIN price
  //
  // That is the 4.62-against-38.25 error this block's own comment predicts,
  // arriving through the one door it did not check. The set of types is taken
  // from the units and the coverage TOGETHER, so a cell is refused when
  // anything it is about to price is a different product.
  const priceIsPerType = model.priceIsPerType === true;
  const allowed = MULTI_TYPE_PRICE_CROPS[cropKey(model.crop)];
  if (!priceIsPerType && allowed) {
    const seen = new Set();
    if (model.typeCode != null) seen.add(String(model.typeCode).trim());
    for (const u of pp) if (u.type_code != null) seen.add(String(u.type_code).trim());
    const bad = [...seen].filter((t) => t && !allowed.has(t));
    if (bad.length) {
      return { available: false, expected: null,
        missing: ["a price for " + (model.crop || "this crop") + " type " + bad.sort().join(" and ")
                + " — RMA publishes this crop at more than one price by type and the "
                + "figure on file is the base one, so pricing this cell would be quoting "
                + "a different product (corn silage is ton/ac, grain is bu/ac)"] };
    }
  }

  // ⚠⚠ CHANGED 2026-10-19 — AN ABSENT ELECTION IS NO LONGER READ AS 100%. It is the rule
  // the prevent-plant engine (ppAdjuster.js) and ppRate.js already hold: a coverage with no
  // election recorded may be a CAT line at 55%, and pricing it full overstates by 1.82x. So
  // it is a named absence here too, and the two figures cannot disagree about it. Measured
  // on the live CY2026 book: 0 of 272 prevent-plant coverages carry a null election, so no
  // figure on screen moves — this closes the door before a row walks through it.
  const rawElec = model.priceElectionPct;
  if (rawElec == null) {
    return { available: false, expected: null,
      missing: ["a price election — the coverage records none, and a CAT line insured at 55% "
              + "priced as 100% would overstate by 1.82x, so it is not assumed full"] };
  }
  const elec = num(rawElec);
  if (!(elec > 0 && elec <= 1)) {
    return { available: false, expected: null,
      missing: ["a usable price election — the coverage states " + JSON.stringify(rawElec)
              + ", and a figure outside 0-100% would scale the estimate silently"] };
  }
  const pxEff = px * elec;

  // The per-acre rate is the ONE rule in ppRate.js — the same call the engine makes per
  // unit — so this column and the adjuster cannot price one acre two ways.
  const at = (f) => dollars(pp.reduce((s, u) =>
    s + cents(ppDollarsPerAcre(num(u.aph_yield), num(u.coverage_level), f, px, elec)
              * num(u.acres) * num(u.share)), 0));

  const lo = at(fLo), hi = at(fHi);
  return {
    available: true, missing: [],
    // A band is an honest answer when the buy-up election is not pinned; a single
    // number pretending to be one is not. `expected` is null unless the factor is exact.
    expected: exact ? lo : null,
    low: lo, high: hi, exact,
    acres: +pp.reduce((s, u) => s + num(u.acres), 0).toFixed(2),
    ppFactor: exact ? fLo : null, ppFactorLow: fLo, ppFactorHigh: fHi,
    projectedPrice: px,
    // The election as applied, so a CAT line can be told from a full one on the
    // row rather than by re-deriving it from the total.
    priceElectionPct: elec,
    effectivePrice: +pxEff.toFixed(4),
    factorBasis: model.factorBasis || null,
    priceBasis: model.priceBasis || null,
    // Shown on the row, so nobody has to take the number on trust.
    why: `${(fLo * 100).toFixed(exact ? 1 : 0)}${exact ? "" : "-" + (fHi * 100).toFixed(0)}% `
       + `PP factor x approved yield x coverage level x $${px} projected price`
       + (elec < 1 ? ` x ${(elec * 100).toFixed(0)}% price election` : "")
       + ` x acres x share`,
  };
}

// ── THE EXPECTED FIGURE ──────────────────────────────────────────────────────
// One cell = one (policy, county, crop). `units` are that cell's crop_coverage_units.
// ⚠ THIS IS THE CARRIER'S OWN STATED GUARANTEE — see `modelledPpExpectation` above for
// the independent one. Both are shown, in their own columns, and never mixed.
export function expectedForCell(units, { stage = null } = {}) {
  const pp = (units || []).filter((u) => u && u.is_prevent_plant);
  const ppLiability = pp.reduce((s, u) => s + cents(u.liability), 0);
  const ppAcres = pp.reduce((s, u) => s + (num(u.acres) || 0), 0);
  if (stage && !isPreventPlantStage(stage)) {
    return { basis: BASIS.NOT_ESTIMATED, expected: null, acres: null,
      why: "a harvest loss pays the guarantee less production to count, and the production "
         + "is a settlement fact this book does not hold — so nothing is estimated for it" };
  }
  if (!pp.length || !(ppLiability > 0)) {
    return { basis: BASIS.NOT_ESTIMATED, expected: null, acres: ppAcres || null,
      why: "the carrier states no prevent-plant guarantee on this coverage's units" };
  }
  return {
    basis: BASIS.PP_STATED,
    expected: dollars(ppLiability),
    acres: +ppAcres.toFixed(2),
    units: pp.length,
    // Everything the number was built from, so the screen can show its working without
    // re-deriving anything.
    why: "the AIP's own stated prevent-plant guarantee on this coverage",
    inputs: pp.map((u) => ({
      unit: [u.unit_number, u.sub_unit].filter(Boolean).join("-") || null,
      acres: num(u.acres), liability: num(u.liability),
      aph: num(u.aph_yield), coverage_level: num(u.coverage_level),
      price_election: num(u.price_election), share: num(u.share),
    })),
  };
}

// ── THE BOOK ─────────────────────────────────────────────────────────────────
//
// Kevin's shape: farm name, then a line per crop AND county, with our expected figure,
// what the AIP paid, and the difference. All claims shown; the ones that agree collapse;
// the rest sort by the biggest dollar gap.
//
// ⚠ TWO LANES, AND THE FIRST ONE IS THE ONE THAT CAUGHT MONROE. A county the carrier
// never paid produces NO line to disagree with, so an amount comparison cannot see it —
// it is found by asking which coverages carrying a loss have no paid line at all. That
// needs no expected figure and no estimate, which is why it is the more reliable half.
//
// ⚠ AND IT IS GATED ON THE FARM HAVING NOTHING OPEN. A prevent-plant acre reported in
// June with the claim still in adjustment in September is NORMAL, not an error. Measured
// 2026-09-24: of 14 CY2026 coverages with prevent-plant acres and no paid line, EIGHT are
// Marquart cells mid-adjustment and three are Woznica's with four notices of loss open.
// Ungated, this lane would open with eleven rows that are simply early — and a work list
// that is mostly noise is one nobody reads.
export function buildExpectedBook({
  lossLines = [], coverages = [], units = [], policies = [], farmers = [],
  openByFarmer = null, cropYear = null,
  // ⚠ OPTIONAL, AND SUPPLIED BY THE CALLER ON PURPOSE. `(coverage, units) => {...}`
  // returning RMA's projected price and prevent-plant factor for that cell. This module
  // must not reach for RMA's tables itself: App.jsx owns `resolveRmaPrice` and
  // `resolvePpFactor`, and a second copy of either is how two engines come to disagree
  // about one farm — which this repo has already paid for twice. Absent, the modelled
  // column is simply not built, and the page says so rather than showing a blank.
  modelInputs = null,
} = {}) {
  const yr = num(cropYear);
  const inYear = (v) => yr == null || num(v) === yr;

  const polById = new Map(policies.map((p) => [p.id, p]));
  const farmName = new Map(farmers.map((f) => [f.id, f.name]));

  // coverage key -> the cell it belongs to
  const covByIif = new Map();
  for (const c of coverages) {
    const p = polById.get(c.policy_id);
    if (!c.iif_key || !p || !inYear(p.crop_year)) continue;
    // ⚠⚠ ONE INSURANCE-IN-FORCE CAN ARRIVE AS SEVERAL ROWS, AND ONE OF THEM MAY
    // CARRY NO GROUND. RMA splits a coverage by PRACTICE, and this map is keyed on
    // the iif alone — so a blind `.set()` keeps whichever row happened to come
    // last and silently drops the other. MARQUART BROS CY2026, measured:
    //
    //   ALLEGANY CORN p713  liability $0        pp null    iif_key 1033793935
    //   ALLEGANY CORN pnull liability $148,438  pp 162.22  iif_key 1033793935
    //
    // Same iif, and the ORGANIC row with no acres on it was the survivor. Every
    // consumer downstream then reads practice 713 off it — which is how the
    // cross-check came to price conventional corn at the ORGANIC $7.30 against
    // $4.62, a ratio of 1.580, on exactly the two cells (ALLEGANY, WYOMING) where
    // this shape exists. LIVINGSTON has a single corn coverage and tied to the
    // dollar, which is the tell.
    //
    // A row that CARRIES GROUND outranks one that does not. It is the narrowest
    // rule that fixes it: no practice code is interpreted, nothing is preferred
    // for being organic or not, and an iif whose rows all carry ground keeps the
    // behaviour it always had.
    const cur = covByIif.get(String(c.iif_key));
    const ground = (x) => (num(x.liability) || 0) > 0 || (num(x.acres) || 0) > 0
                       || (num(x.pp_acres) || 0) > 0;
    if (cur && ground(cur) && !ground(c)) continue;
    covByIif.set(String(c.iif_key), { ...c, aip: p.aip, policy_number: p.policy_number, farmer_id: p.farmer_id, crop_year: p.crop_year });
  }
  // (policy, county, crop) -> units
  const unitsByCell = new Map();
  for (const u of units) {
    if (!inYear(u.crop_year)) continue;
    const k = [u.policy_id, u.county, u.crop].join("|");
    if (!unitsByCell.has(k)) unitsByCell.set(k, []);
    unitsByCell.get(k).push(u);
  }

  const { kept, superseded } = currentLines((lossLines || []).filter((r) => inYear(r.crop_year)));

  // paid, per coverage per stage — on the CURRENT generation only
  const paid = new Map();
  for (const l of kept) {
    const k = [String(l.iif_key), String(l.stage_code || "")].join("|");
    if (!paid.has(k)) paid.set(k, { cents: 0, lines: 0, acres: 0 });
    const e = paid.get(k);
    e.cents += cents(l.aip_indemnity);
    e.lines++;
    e.acres += num(l.determined_acreage) || 0;
  }

  // every cell we can speak about: a coverage carrying prevent-plant acres, or one the
  // carrier has paid something on. A coverage with neither is not a claim.
  const cells = new Map();
  const cellOf = (cov) => {
    const k = [cov.farmer_id, cov.county, cov.crop].join("|");
    if (!cells.has(k)) cells.set(k, {
      key: k, farmerId: cov.farmer_id, farm: farmName.get(cov.farmer_id) || null,
      county: cov.county, crop: cov.crop, aip: cov.aip, policyNumber: cov.policy_number,
      cropYear: cov.crop_year, iifKeys: [], stages: [], expected: null, paid: 0,
    });
    const c = cells.get(k);
    if (cov.iif_key && !c.iifKeys.includes(String(cov.iif_key))) c.iifKeys.push(String(cov.iif_key));
    return c;
  };

  // ⚠ THE EXPECTED FIGURE IS COUNTED ONCE PER (POLICY, COUNTY, CROP), NOT ONCE PER
  // COVERAGE. One cell carries the base MPCI coverage AND its SCO and ECO overlays —
  // three `iif_key`s over the SAME county and crop, all resolving to the SAME
  // `crop_coverage_units` rows. Added per coverage, the stated guarantee is multiplied by
  // however many overlays the grower elected, and the difference column then reads as a
  // carrier underpayment of exactly 1/2 or 1/3.
  //
  // Not hypothetical — it is what the first run of this builder produced, on the live
  // book: RAUBERDALE 376,206 expected against 125,402 paid (exactly 3.000), BRANDON M
  // BRADY, KLUDT, LYNN-ETTE, KOCH LIBRA and HOWLETT all at exactly 3.000, HANSEN and
  // SILSBY at exactly 2.000. Sixty-nine cells and $4.1M of invented shortfall, every one
  // of them a grower who had been paid in full. An integer ratio is the tell.
  //
  // The same trap this file already records for `matchSupplementals` ($3.3M of liability
  // double-rendered on 31 cards) and for `policy_fields` unit labels — an overlay is not
  // a second coverage of the ground, it is a second product on the same ground.
  // ── ⚠ THE FACTOR COMES OFF THE BASE COVERAGE, NEVER WHICHEVER ARRIVED FIRST ──
  //
  // `cell.modelled` is set by the first coverage to reach a cell, and `covByIif`
  // holds EVERY coverage with an iif — overlays included. An SCO or ECO row is a
  // second product on the same ground, not a second coverage of it, and it
  // carries no `insurance_options`: reach the cell through one and the
  // prevent-plant factor resolves `not_captured`, which is a BAND (corn
  // 0.55–0.65) rather than a point, so the farm rollup has nothing to total.
  //
  // Measured over the live CY2026 book: the factor is exact on **94 of 105**
  // cells off the first-arriving coverage and on **105 of 105** off the base
  // one — 11 cells silently reduced to a range by arrival order alone.
  //
  // This is the first-wins class this file already records three times, on
  // `type_code`, on `unit_number` and on the P15 yield key. Arrival order is not
  // a tie-break; it is the file's own ordering deciding a grower's number.
  // ⚠⚠ AND AN EMPTY SHELL MUST NOT DECIDE THE PRICE. A cell can hold MORE THAN
  // ONE base-plan coverage, and one of them may carry no ground at all —
  // MARQUART BROS CY2026 holds, in both ALLEGANY and WYOMING:
  //
  //     CORN t016 p713  plan 02   acres null   pp null   liability $0   <- organic
  //     CORN t016 pnull plan 02   acres 349.4  pp 162.22 liability $148,438
  //
  // First-seen picked the ORGANIC shell, so practice 713 reached the price
  // lookup and answered with organic corn at $7.30 against the conventional
  // $4.62 — a ratio of 1.580, which is exactly what those two cells read at on
  // the cross-check while LIVINGSTON, which has only one corn coverage, tied to
  // the dollar. Same first-wins class as the overlay above, one level finer.
  //
  // So a coverage that CARRIES GROUND outranks one that does not. It is the
  // narrowest rule that fixes it: no practice is interpreted, nothing is
  // preferred on the strength of its practice code, and a cell whose coverages
  // all carry ground is unaffected.
  const BASE_PLAN_CODES = new Set(["01", "02", "03", "90"]);
  const carriesGround = (c) => (num(c.liability) || 0) > 0 || (num(c.acres) || 0) > 0
                            || (num(c.pp_acres) || 0) > 0;
  const baseCovByCell = new Map();
  for (const [, c] of covByIif) {
    if (!BASE_PLAN_CODES.has(String(c.mpci_plan_code || ""))) continue;
    const k = [c.policy_id, c.county, c.crop].join("|");
    const cur = baseCovByCell.get(k);
    if (!cur || (!carriesGround(cur) && carriesGround(c))) baseCovByCell.set(k, c);
  }

  const expectedCounted = new Set();
  for (const [iif, cov] of covByIif) {
    const cellUnitKey = [cov.policy_id, cov.county, cov.crop].join("|");
    const u = unitsByCell.get(cellUnitKey) || [];
    const hasPp = (num(cov.pp_acres) || 0) > 0 || u.some((x) => x.is_prevent_plant);
    const stagesPaid = [...paid.keys()].filter((k) => k.startsWith(iif + "|"));
    if (!hasPp && !stagesPaid.length) continue;

    const cell = cellOf(cov);
    const exp = expectedForCell(u, { stage: "PF" });
    // OUR OWN figure, from RMA's price and factor — see modelledPpExpectation. Guarded by
    // the same once-per-cell key as the stated one, so an overlay coverage on the same
    // ground cannot count it twice.
    // The factor is a property of the COVERAGE the grower elected, so it is read
    // off the base row for this cell where one exists; `cov` remains the fallback
    // so a cell described only by an overlay still gets an answer (and a band)
    // rather than nothing. See the note above `baseCovByCell`.
    const factorCov = baseCovByCell.get(cellUnitKey) || cov;
    const mod = modelInputs ? modelledPpExpectation(u, modelInputs(factorCov, u) || {}) : null;
    if (exp.basis === BASIS.PP_STATED && !expectedCounted.has(cellUnitKey)) {
      expectedCounted.add(cellUnitKey);
      cell.expected = (cell.expected || 0) + exp.expected;
      cell.expectedDetail = exp;
      if (mod) cell.modelled = mod;
    } else if (!cell.expectedDetail) {
      cell.expectedDetail = exp;
      if (mod && !cell.modelled) cell.modelled = mod;
    }
    for (const k of stagesPaid) {
      const st = k.split("|")[1];
      const e = paid.get(k);
      cell.paid += dollars(e.cents);
      cell.stages.push({ stage: st || "(none)", paid: dollars(e.cents), lines: e.lines, acres: +e.acres.toFixed(2),
        isPp: isPreventPlantStage(st) });
    }
  }

  // classify
  const open = openByFarmer instanceof Map ? openByFarmer : new Map();
  const rows = [...cells.values()].map((c) => {
    const paidPp = c.stages.filter((s) => s.isPp).reduce((s, x) => s + x.paid, 0);
    const hasAnyPaid = c.stages.length > 0;
    const expected = c.expected;
    const diff = expected != null && hasAnyPaid ? +(paidPp - expected).toFixed(2) : null;
    let lane;
    if (!hasAnyPaid) lane = LANE.NOTHING_PAID;
    else if (expected == null) lane = LANE.NOT_ESTIMATED;
    else if (Math.abs(cents(paidPp) - cents(expected)) < 100) lane = LANE.AGREES;   // within $1
    else lane = LANE.DIFFERS;
    return {
      ...c, expected, paidPp, difference: diff, lane,
      // A farm still in adjustment is not a farm that was underpaid.
      farmHasOpenClaim: !!open.get(c.farmerId),
      actionable: lane === LANE.DIFFERS || (lane === LANE.NOTHING_PAID && !open.get(c.farmerId)),
    };
  });

  // farm -> its cells, biggest gap first
  const byFarm = new Map();
  for (const r of rows) {
    const k = r.farmerId || "(unknown)";
    if (!byFarm.has(k)) byFarm.set(k, { farmerId: r.farmerId, farm: r.farm, cells: [], worst: 0, expected: 0, paid: 0, actionable: 0 });
    const f = byFarm.get(k);
    f.cells.push(r);
    f.expected += r.expected || 0;
    f.paid += r.paidPp || 0;
    if (r.difference != null && Math.abs(r.difference) > Math.abs(f.worst)) f.worst = r.difference;
    if (r.actionable) f.actionable++;
  }
  const farms = [...byFarm.values()].map((f) => ({
    ...f,
    cells: f.cells.slice().sort((a, b) => Math.abs(b.difference || 0) - Math.abs(a.difference || 0)),
    // A farm every one of whose cells agrees collapses — that is Kevin's "claims that
    // match exactly can be collapsed".
    allAgree: f.cells.every((c) => c.lane === LANE.AGREES),
  })).sort((a, b) => (b.actionable - a.actionable) || (Math.abs(b.worst) - Math.abs(a.worst)));

  const tally = (l) => rows.filter((r) => r.lane === l).length;
  return {
    farms, rows,
    supersededLines: superseded.length,
    supersededDollars: +dollars(superseded.reduce((s, r) => s + cents(r.aip_indemnity), 0)).toFixed(2),
    totals: {
      cells: rows.length,
      agrees: tally(LANE.AGREES),
      differs: tally(LANE.DIFFERS),
      nothingPaid: tally(LANE.NOTHING_PAID),
      notEstimated: tally(LANE.NOT_ESTIMATED),
      actionable: rows.filter((r) => r.actionable).length,
      unexplained: +rows.filter((r) => r.lane === LANE.DIFFERS)
        .reduce((s, r) => s + Math.abs(r.difference || 0), 0).toFixed(2),
    },
  };
}
